import React, { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { formatDateDMY } from '../../utils/dateFormat';

/**
 * DocumentPrintView — a clean, simple A4 printable layout used by both
 * Purchase Orders and Purchase Entries.
 *
 * Props:
 *   title       — "Purchase Order" or "Purchase Entry / Goods Receipt"
 *   docNumber   — PO# / Entry#
 *   docDate     — ISO date string
 *   company     — { name, logo_url, address, phone, email, tax_id }
 *   parties     — array of { label, lines: string[] }  (e.g. From / To)
 *   meta        — array of { label, value } pairs shown in a 2-column block
 *   itemColumns — array of { key, label, align? 'left'|'right' }
 *   items       — array of row objects
 *   totals      — array of { label, value, bold? }
 *   notes       — optional string
 *   onClose     — callback
 */
const DocumentPrintView = ({
  title,
  docNumber,
  docDate,
  company,
  parties = [],
  meta = [],
  itemColumns = [],
  items = [],
  totals = [],
  notes,
  onClose,
  /**
   * Header style:
   *   - 'letterhead' (default): blank 45mm top reserved for physical letterhead.
   *   - 'statement': renders company on LEFT, customer on RIGHT, with a
   *     centered heading (used for Customer Ledger / Statement of Accounts).
   */
  headerLayout = 'letterhead',
  /** Centered heading to render in 'statement' layout (defaults to title). */
  centerHeading,
  /**
   * In 'statement' layout, customer info appears on the right of the header
   * block. Pass the customer object with name/address/phone/email/vat_reg_no.
   */
  customer,
  /**
   * Watermark label (e.g. 'DRAFT', 'PENDING'). When set, a diagonal pale-red
   * watermark is overlaid on the printed sheet. Pass falsy to hide.
   */
  watermark,
  /**
   * Signature info for the bottom signature row.
   *   preparedBy = { name, signature_url? }
   *   approvers  = [ { name, signature_url?, level }, ... ]  (0..2 entries)
   * When omitted, the column shows only the label + a blank line.
   */
  preparedBy,
  approvers,
}) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    doc.open();
    // Keep document title empty so browser print headers don't show a doc title.
    doc.write('<!DOCTYPE html><html><head><title></title></head><body><div id="root"></div></body></html>');
    doc.close();
  }, [title, docNumber]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const root = iframe.contentDocument?.getElementById('root');
    if (!root) return;
    const reactRoot = createRoot(root);
    reactRoot.render(
      <PrintLayout
        title={title}
        docNumber={docNumber}
        docDate={docDate}
        company={company}
        parties={parties}
        meta={meta}
        itemColumns={itemColumns}
        items={items}
        totals={totals}
        notes={notes}
        headerLayout={headerLayout}
        centerHeading={centerHeading}
        customer={customer}
        watermark={watermark}
        preparedBy={preparedBy}
        approvers={approvers}
      />
    );
    const t = setTimeout(() => {
      // Wait for every image in the iframe (logo, header, footer, signatures)
      // to load before firing print. Otherwise the first print can capture a
      // half-rendered layout — only the cached logo shows.
      const idoc = iframe.contentDocument;
      if (!idoc) return;
      const imgs = Array.from(idoc.images || []);
      const waiters = imgs.map((img) => (
        img.complete && img.naturalWidth > 0
          ? Promise.resolve()
          : new Promise((res) => {
              img.addEventListener('load', res, { once: true });
              img.addEventListener('error', res, { once: true });
              setTimeout(res, 4000);
            })
      ));
      Promise.all(waiters).then(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch {
          /* user can still click Print */
        }
      });
    }, 200);
    return () => {
      clearTimeout(t);
      // Defer unmount to a microtask so we don't trigger a synchronous
      // unmount during React's render phase (causes a noisy warning).
      setTimeout(() => {
        try { reactRoot.unmount(); } catch { /* noop */ }
      }, 0);
    };
  }, [title, docNumber, docDate, company, parties, meta, itemColumns, items, totals, notes, headerLayout, centerHeading, customer, preparedBy, approvers]);

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" data-testid="doc-print-modal">
      <div className="bg-white rounded shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
        <div className="px-4 py-2 border-b flex items-center justify-between">
          <h3 className="font-semibold">Print preview · {title} {docNumber}</h3>
          <div className="flex gap-2">
            <button
              onClick={() => iframeRef.current?.contentWindow?.print()}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              data-testid="doc-print-btn"
            >
              🖨 Print / Save as PDF
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 border rounded text-sm hover:bg-gray-50"
              data-testid="doc-print-close-btn"
            >
              Close
            </button>
          </div>
        </div>
        <iframe ref={iframeRef} title="Document print" className="flex-1 w-full bg-gray-200" />
      </div>
    </div>
  );
};

const PrintLayout = ({ title, docNumber, docDate, company, parties, meta, itemColumns, items, totals, notes, headerLayout, centerHeading, customer, watermark, preparedBy, approvers }) => (
  <>
    <style>{`
      /* @page margins are set to 0 so browsers don't inject URL / title /
         page-number text inside the printable area. We then add our own
         padding inside .doc to leave room for a pre-printed company
         letterhead at the top and footer space at the bottom. */
      @page { size: A4; margin: 0; }
      html, body { margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .doc {
        position: relative;
        display: flex;
        flex-direction: column;
        min-height: 297mm; /* full A4 height */
        padding: 55mm 14mm 30mm 14mm; /* 55mm top reserves room for logo + company name + multi-line address */
        box-sizing: border-box;
        font-size: 12px;
      }
      .watermark {
        position: absolute; inset: 0;
        display: flex; align-items: center; justify-content: center;
        pointer-events: none;
        z-index: 5;
      }
      .watermark span {
        font-weight: 900;
        font-size: 180pt;
        color: rgba(180,30,30,0.10);
        transform: rotate(-30deg);
        letter-spacing: 0.1em;
        white-space: nowrap;
      }
      /* Document-header image uploaded from Company Settings. Sits in the
         TOP-RIGHT corner only (~60mm wide). The TOP-LEFT shows the existing
         company logo + name + address. Footer banner spans the full bottom. */
      .doc-banner-top, .doc-banner-bottom {
        position: absolute;
        z-index: 1;
      }
      .doc-banner-top {
        top: 8mm;
        right: 12mm;
        width: 60mm;
        height: 28mm;
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }
      .doc-banner-bottom {
        bottom: 0;
        left: 0;
        right: 0;
        text-align: center;
      }
      .doc-banner-top img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
      .doc-banner-bottom img { width: 100%; max-height: 22mm; object-fit: contain; display: block; }
      .doc-logo-left {
        position: absolute;
        top: 8mm; left: 12mm;
        width: 86mm;
      }
      .doc-logo-left .logo-box {
        width: 60mm; height: 28mm;
        display: flex; align-items: center; justify-content: flex-start;
      }
      .doc-logo-left .logo-box img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
      .doc-logo-left .reg-block {
        margin-top: 2mm;
        font-size: 9pt;
        color: #111;
        line-height: 1.45;
      }
      .doc-logo-left .reg-block .row { display: flex; gap: 4mm; flex-wrap: wrap; }
      .doc-logo-left .reg-block .lbl { color: #6b7280; }
      .doc-logo-left .reg-block .val { font-weight: 700; color: #111; }
      .doc-body { flex: 1 0 auto; }
      .doc h1 { font-size: 20px; margin: 0 0 12px 0; font-weight: 700; text-align: right; }
      .doc-title-row { border-bottom: 2px solid #111; padding-bottom: 6px; margin-bottom: 10px; }
      /* Statement-of-Accounts header (company left / customer right / centered heading) */
      .soa-header { display: grid; grid-template-columns: 1fr 1fr; gap: 24px; margin-bottom: 6px; }
      .soa-header-statement { grid-template-columns: 1fr; }
      .soa-block { font-size: 11px; line-height: 1.45; }
      .soa-block .lbl {
        color: #111;
        font-size: 16px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 1px;
        margin-bottom: 6px;
      }
      .soa-block .name { font-weight: 700; font-size: 13px; margin-bottom: 2px; }
      .soa-block.right { text-align: right; }
      .soa-heading {
        text-align: center;
        font-size: 18px;
        font-weight: 700;
        letter-spacing: 2px;
        margin: 14px 0 6px 0;
        padding: 8px 0;
        border-top: 2px solid #111;
        border-bottom: 2px solid #111;
        text-transform: uppercase;
      }
      .soa-period { text-align: center; font-size: 11px; color: #374151; margin-bottom: 8px; }
      table { width: 100%; border-collapse: collapse; margin-top: 8px; }
      thead th { background: #1f2937; color: #fff; padding: 6px 8px; font-size: 11px; text-align: left; }
      thead th.right { text-align: right; }
      tbody td { padding: 6px 8px; border-bottom: 1px solid #e5e7eb; vertical-align: top; font-size: 11px; }
      tbody td.right { text-align: right; }
      .totals { margin-top: 10px; width: 320px; margin-left: auto; }
      .totals .line { display: flex; justify-content: space-between; padding: 3px 0; font-size: 12px; }
      .totals .line.bold { font-weight: 700; border-top: 1px solid #111; padding-top: 6px; }
      .meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 16px; margin-top: 10px; font-size: 11px; }
      .meta-grid div span:first-child { color: #6b7280; }
      .notes { margin-top: 14px; padding: 8px; background: #f3f4f6; font-size: 11px; border-radius: 4px; white-space: pre-wrap; }
      .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 14px; }
      .parties .party { padding: 8px; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 11px; }
      .parties .party .lbl { color: #6b7280; font-size: 10px; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 4px; }
      .parties .party .ln { line-height: 1.45; }
      .parties .party .ln.name { font-weight: 800; font-size: 13px; color: #111; margin-bottom: 2px; }
      /* Signature block pinned to the bottom of the printable area.
         margin-top: auto pushes it down inside the flex .doc; the .doc's
         30mm padding-bottom leaves a 3cm gap to the page edge. */
      .signature-block {
        display: flex;
        justify-content: space-between;
        margin-top: auto;
        padding-top: 16px;
        gap: 24px;
        page-break-inside: avoid;
      }
      .signature-block .sig {
        flex: 1;
        text-align: center;
        font-size: 11px;
        color: #374151;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
      }
      .signature-block .sig .stamp {
        height: 18mm;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        margin-bottom: 2mm;
      }
      .signature-block .sig .stamp img {
        max-height: 18mm;
        max-width: 90%;
        object-fit: contain;
      }
      /* Two side-by-side signature slots inside a single Approved-by cell. */
      .signature-block .sig .stamp-dual {
        gap: 4mm;
      }
      .signature-block .sig .stamp-dual .stamp-slot {
        flex: 1;
        display: flex;
        align-items: flex-end;
        justify-content: center;
        height: 100%;
      }
      .signature-block .sig .stamp-dual .stamp-slot img {
        max-height: 18mm;
        max-width: 100%;
        object-fit: contain;
      }
      .signature-block .sig .line {
        border-top: 1px solid #111;
        margin-top: 0;
        padding-top: 4px;
      }
      .signature-block .sig .role {
        font-weight: 700;
        font-size: 11px;
        color: #111;
        text-transform: none;
        letter-spacing: 0;
      }
      .signature-block .sig .date-row {
        margin-top: 4px;
        font-size: 9.5px;
        color: #555;
        text-align: center;
      }
      /* Print-only overrides — placed LAST so they win source-order. */
      @media print {
        .doc {
          height: 297mm;
          min-height: 297mm;
          padding-bottom: 80mm; /* reserve room for fixed signature block (~28mm) + footer banner (~22mm) + gap */
        }
        .doc-banner-bottom {
          position: fixed !important;
          bottom: 0 !important;
          left: 0;
          right: 0;
        }
        .signature-block {
          position: fixed !important;
          bottom: 28mm !important; /* sits just above the footer banner */
          left: 14mm;
          right: 14mm;
          margin-top: 0 !important;
          padding-top: 0 !important;
        }
      }
    `}</style>
    <div className="doc">
      {/* TOP-LEFT corner: company logo + CR / VAT registration lines. Shown
          on every layout (PO/PE/Invoice/DN/Statement). Company name and
          address are intentionally omitted here — they belong in the
          uploaded footer banner. */}
      {(company?.logo_url || company?.cr_number || company?.cr_number_2 || company?.tax_id) && (
        <div className="doc-logo-left">
          {company.logo_url && (
            <div className="logo-box">
              <img src={company.logo_url} alt={company.name || ''} />
            </div>
          )}
          {(company.cr_number || company.cr_number_2 || company.tax_id) && (
            <div className="reg-block">
              {company.cr_number && (
                <div><span className="lbl">CR No 1:</span> <span className="val">{company.cr_number}</span></div>
              )}
              {company.cr_number_2 && (
                <div><span className="lbl">CR No 2:</span> <span className="val">{company.cr_number_2}</span></div>
              )}
              {company.tax_id && (
                <div><span className="lbl">VAT No:</span> <span className="val">{company.tax_id}</span></div>
              )}
            </div>
          )}
        </div>
      )}
      {/* TOP-RIGHT corner: uploaded Document Header banner (~60mm wide) */}
      {company?.document_header_url && (
        <div className="doc-banner-top"><img src={company.document_header_url} alt="" /></div>
      )}
      {company?.document_footer_url && (
        <div className="doc-banner-bottom"><img src={company.document_footer_url} alt="" /></div>
      )}
      {watermark && (
        <div className="watermark"><span>{watermark}</span></div>
      )}
      <div className="doc-body">
        {headerLayout === 'statement' ? (
          <>
            {/* Statement-of-Accounts: company info already shown top-left via logo block.
                Here we render only the Customer "To" block, centered heading, and period. */}
            <div className="soa-header soa-header-statement">
              <div className="soa-block">
                <div className="lbl">To</div>
                <div className="name">{customer?.name || ''}</div>
                {customer?.address && <div>{customer.address}</div>}
                {(customer?.phone || customer?.email) && (
                  <div>
                    {customer.phone ? `Tel: ${customer.phone}` : ''}
                    {customer.email ? `${customer.phone ? ' · ' : ''}${customer.email}` : ''}
                  </div>
                )}
                {customer?.vat_reg_no && <div>VAT: {customer.vat_reg_no}</div>}
              </div>
            </div>
            <div className="soa-heading">{centerHeading || title}</div>
            {docDate && (
              <div className="soa-period">
                {customer?.period_label
                  ? customer.period_label
                  : `As of ${formatDateDMY(docDate)}`}
              </div>
            )}
          </>
        ) : (
          <div className="doc-title-row">
            <h1>{prettyTitle(title, docNumber)}</h1>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', fontSize: 11, color: '#374151' }}>
              <div>Date: {docDate ? formatDateDMY(docDate) : '-'}</div>
            </div>
          </div>
        )}

        {parties && parties.length > 0 && (
          <div className="parties">
            {parties.map((p, i) => {
              const lines = (p.lines || []).filter(Boolean);
              return (
                <div key={i} className="party">
                  <div className="lbl">{p.label}</div>
                  {lines.map((ln, j) => (
                    <div key={j} className={j === 0 ? 'ln name' : 'ln'}>{ln}</div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {meta && meta.length > 0 && (
          <div className="meta-grid">
            {meta.map((m, i) => (
              <div key={i}>
                <span>{m.label}:</span> <span style={{ marginLeft: 6 }}>{m.value || '-'}</span>
              </div>
            ))}
          </div>
        )}

        <table>
          <thead>
            <tr>
              <th>#</th>
              {itemColumns.map((c) => (
                <th key={c.key} className={c.align === 'right' ? 'right' : ''}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => {
              const serials = Array.isArray(it.serial_numbers) ? it.serial_numbers : [];
              return (
              <React.Fragment key={idx}>
              <tr>
                <td>{idx + 1}</td>
                {itemColumns.map((c) => (
                  <td key={c.key} className={c.align === 'right' ? 'right' : ''}>
                    {formatCell(it[c.key], c)}
                  </td>
                ))}
              </tr>
              {serials.length > 0 && (
                <tr>
                  <td></td>
                  <td colSpan={itemColumns.length} style={{ fontSize: 10, color: '#475569', borderTop: '1px dashed #cbd5e1' }}>
                    <b>S/N:</b> {serials.join(', ')}
                  </td>
                </tr>
              )}
              </React.Fragment>
            );
            })}
            {items.length === 0 && (
              <tr><td colSpan={itemColumns.length + 1} style={{ textAlign: 'center', color: '#9ca3af' }}>No items</td></tr>
            )}
          </tbody>
        </table>

        {totals && totals.length > 0 && (
          <div className="totals">
            {totals.map((t, i) => (
              <div key={i} className={`line ${t.bold ? 'bold' : ''}`}>
                <span>{t.label}</span>
                <span>{t.value}</span>
              </div>
            ))}
          </div>
        )}

        {notes && <div className="notes"><b>Notes:</b> {notes}</div>}
      </div>

      {/* Signatures pinned to the bottom of the printable area
          (hidden for Statement-of-Accounts style which doesn't need them). */}
      {headerLayout !== 'statement' && (
        <div className="signature-block">
          <SigCell role="Prepared by" person={preparedBy} dateStr={fmtDateDoc(docDate)} />
          <ApprovedCell approvers={approvers} />
          <SigCell role="Received by" person={null} />
        </div>
      )}
    </div>
  </>
);

// Title shown on the document — formatted as "Purchase Order 454" rather
// than "PURCHASE ORDER" + the number on a separate line.
const prettyTitle = (title, docNumber) => {
  const t = (title || '').replace(/\s*\/\s*Goods Receipt\s*$/i, '');
  return docNumber ? `${t} ${docNumber}` : t;
};

const SigCell = ({ role, person, dateStr }) => (
  <div className="sig">
    <div className="stamp">
      {person?.signature_url ? <img src={person.signature_url} alt="" /> : null}
    </div>
    <div className="line">
      <div className="role">{role}</div>
    </div>
    <div className="date-row">Date: {dateStr || '____________________'}</div>
  </div>
);

const ApprovedCell = ({ approvers }) => {
  const list = Array.isArray(approvers) ? approvers : [];
  if (list.length <= 1) {
    const p = list[0] || null;
    return <SigCell role="Approved by" person={p} dateStr={fmtDateDoc(p?.at)} />;
  }
  // Two-level approval — show both signature images side-by-side above
  // a single shared "Approved by" line/label, with per-level dates below.
  return (
    <div className="sig">
      <div className="stamp stamp-dual">
        {list.map((p, i) => (
          <div key={i} className="stamp-slot">
            {p?.signature_url ? <img src={p.signature_url} alt="" /> : null}
          </div>
        ))}
      </div>
      <div className="line">
        <div className="role">Approved by</div>
      </div>
      <div className="date-row">
        Date:&nbsp;
        {list.map((p, i) => (
          <span key={i}>
            {i > 0 ? ' · ' : ''}{fmtDateDoc(p?.at) || '—'}
          </span>
        ))}
      </div>
    </div>
  );
};

const fmtDateDoc = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s).slice(0, 10);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const formatCell = (v, col) => {
  if (v === null || v === undefined || v === '') return '-';
  if (col.numeric && typeof v === 'number') return v.toFixed(col.decimals ?? 3);
  return String(v);
};

export default DocumentPrintView;
